import React from 'react';
import { ArrowRight } from 'lucide-react';
import { SAVING_MESSAGES } from '../utils/savingMessages';

interface NextButtonProps {
  isLoading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary';
  className?: string;
}

/**
 * Brings the saving messages into view when saving starts, so a client who
 * clicked Next further up the page can see them. Centred, because the bottom
 * edge sits under the fixed help button. A stable function, so React calls it
 * once when the messages appear rather than on every render.
 */
const scrollIntoViewOnMount = (el: HTMLElement | null) =>
  el?.scrollIntoView({ behavior: 'smooth', block: 'center' });

/**
 * Reassuring copy shown under the button while saving; it changes over time
 * via CSS (see `.onboarding-saving-*` in index.css).
 */
const SavingMessages: React.FC = () => (
  <>
    <span
      ref={scrollIntoViewOnMount}
      className="onboarding-saving-messages text-base font-medium text-muted-foreground"
      aria-hidden="true"
    >
      {SAVING_MESSAGES.map((message, index) => {
        const next = SAVING_MESSAGES[index + 1];
        return (
          <span
            key={message.afterMs}
            className={`onboarding-saving-message${next ? '' : ' onboarding-saving-message--last'}`}
            style={{
              animationDelay: `${message.afterMs}ms`,
              ...(next && { animationDuration: `${next.afterMs - message.afterMs}ms` }),
            }}
          >
            {message.text}
          </span>
        );
      })}
    </span>
  </>
);

export const NextButton: React.FC<NextButtonProps> = ({
  isLoading = false,
  disabled = false,
  onClick,
  type = 'submit',
  children,
  variant = 'primary',
  className = '',
}) => {
  const variantStyles = {
    primary:
      'h-12 px-8 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-300 hover:to-yellow-400 text-gray-900 shadow-lg hover:shadow-xl text-lg',
    secondary:
      'h-12 px-6 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 text-gray-700 dark:text-gray-300 border-2 border-gray-300 dark:border-white/15 hover:border-gray-400 dark:hover:border-white/25',
  };

  return (
    <div className="flex flex-col items-end gap-2 pt-4 border-t dark:border-white/10">
      <button
        type={type}
        disabled={disabled || isLoading}
        onClick={onClick}
        // While saving, keep full colour (not the faded disabled look).
        className={`${variantStyles[variant]} font-semibold transition-all inline-flex items-center gap-2 rounded-lg ${
          isLoading ? 'cursor-wait' : 'disabled:opacity-50 disabled:cursor-not-allowed'
        } ${className}`}
      >
        {isLoading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current" />
            {children || 'Saving…'}
          </>
        ) : (
          <>
            {children || 'Next'}
            {variant === 'primary' && <ArrowRight className="w-5 h-5" />}
          </>
        )}
      </button>
      {isLoading && variant === 'primary' && <SavingMessages />}
    </div>
  );
};
